/**
 * Thin wrapper over the Web Speech API's synthesis half.
 *
 * Voice lists load asynchronously and inconsistently across browsers, so
 * `onVoices` fires again whenever the list changes rather than resolving once.
 */

export interface VoiceOption {
  uri: string
  name: string
  lang: string
}

export function speechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window
}

function allVoices(): SpeechSynthesisVoice[] {
  if (!speechSupported()) return []
  return window.speechSynthesis.getVoices()
}

/**
 * Voices for one language, the closest regional match first.
 *
 * `speech` is a full tag like `zh-CN` or `de-DE`. Exact matches rank above
 * other regions of the same language, so a German learner gets de-DE ahead of
 * de-AT, but still gets *something* rather than silence if only de-AT exists.
 */
export function voicesFor(speech: string): VoiceOption[] {
  const wanted = speech.toLowerCase()
  const base = wanted.split('-')[0]

  return allVoices()
    .filter((v) => v.lang.toLowerCase().startsWith(base))
    .sort((a, b) => {
      const rank = (l: string) => (l.toLowerCase() === wanted ? 0 : 1)
      return rank(a.lang) - rank(b.lang) || a.name.localeCompare(b.name)
    })
    .map((v) => ({ uri: v.voiceURI, name: v.name, lang: v.lang }))
}

export function onVoices(speech: string, cb: (voices: VoiceOption[]) => void): () => void {
  if (!speechSupported()) {
    cb([])
    return () => {}
  }
  const emit = () => cb(voicesFor(speech))
  emit()
  window.speechSynthesis.addEventListener('voiceschanged', emit)
  return () => window.speechSynthesis.removeEventListener('voiceschanged', emit)
}

export interface SpeakOptions {
  /** BCP-47 tag of the language being spoken. Defaults to Mandarin. */
  speech?: string
  voiceURI?: string | null
  rate?: number
  onEnd?: () => void
  onError?: () => void
  /**
   * Called when the voice list finally arrives and this language has no voice
   * in it. The synchronous return cannot tell you that yet on a cold page.
   */
  onUnavailable?: () => void
}

export function cancelSpeech(): void {
  if (speechSupported()) window.speechSynthesis.cancel()
}

/**
 * How long to wait for a browser that has not produced its voice list yet
 * before concluding there is genuinely nothing to speak with. Chrome delivers
 * the list within a few hundred milliseconds of the first touch; two seconds is
 * slow enough to be safe and quick enough that a real "no voice installed"
 * message still arrives while the user is looking at the button.
 */
const VOICE_WAIT_MS = 2000

/**
 * Speak text in a given language. Returns false when synthesis is unavailable
 * or no voice for that language is installed, so callers can surface a real
 * explanation instead of failing silently.
 *
 * A `true` return can mean "speaking now" or "speaking as soon as the voice
 * list loads" — see `onUnavailable`, which is how the deferred case reports a
 * failure the synchronous return could not yet know about.
 */
export function speak(text: string, opts: SpeakOptions = {}): boolean {
  if (!speechSupported() || !text.trim()) return false

  const speech = opts.speech ?? 'zh-CN'
  const base = speech.toLowerCase().split('-')[0]

  const attempt = (): boolean => {
    const candidates = allVoices().filter((v) => v.lang.toLowerCase().startsWith(base))
    if (candidates.length === 0) return false

    window.speechSynthesis.cancel()

    const utter = new SpeechSynthesisUtterance(text)
    // A saved preference only applies if it belongs to this language; otherwise a
    // Mandarin voice would be asked to read German.
    const chosen =
      (opts.voiceURI && candidates.find((v) => v.voiceURI === opts.voiceURI)) || candidates[0]
    utter.voice = chosen
    utter.lang = chosen.lang
    /*
     * The floor is 0.3, not 0.4.
     *
     * 0.4 was my judgement about where browser voices stop separating words and
     * start slurring, and it holds for listening. Shadowing is a different job:
     * you are speaking over the line, and you need room between the syllables
     * more than you need each one pretty. Slur is an acceptable price for that,
     * and the person doing the drill can hear whether it is working.
     *
     * Still clamped rather than trusted — a stored setting from an older build,
     * or a hand-edited export, could be anything.
     */
    utter.rate = Math.min(2, Math.max(0.3, opts.rate ?? 1))
    if (opts.onEnd) utter.onend = opts.onEnd
    if (opts.onError) utter.onerror = opts.onError

    window.speechSynthesis.speak(utter)
    return true
  }

  if (attempt()) return true

  // An empty list means the browser has not finished loading voices yet, not
  // that this language is missing one. Chrome returns nothing from the first
  // getVoices() call and fires `voiceschanged` when the real list lands, so a
  // first click used to be told "no voice installed" — wrong, and sticky.
  // Wait for the list and speak then; only report a failure once we know what
  // is actually installed.
  if (allVoices().length === 0) {
    let timer: ReturnType<typeof setTimeout>
    const done = () => {
      window.speechSynthesis.removeEventListener('voiceschanged', onReady)
      clearTimeout(timer)
    }
    const onReady = () => {
      done()
      if (!attempt()) opts.onUnavailable?.()
    }
    timer = setTimeout(() => {
      done()
      opts.onUnavailable?.()
    }, VOICE_WAIT_MS)
    window.speechSynthesis.addEventListener('voiceschanged', onReady)
    return true
  }

  return false
}

// Touching the list at startup is what starts the asynchronous load in Chrome,
// so the voices are usually already in hand by the time anyone clicks Play.
// Without this the deferred path above carries every first click on the page.
if (speechSupported()) window.speechSynthesis.getVoices()
